// Compound Quest — game data
// Same four-phase shape as Budget Commander and Time Raid: Recon (tag real
// attributes) -> Planning (commit to a strategy) -> Campaign (defend it
// through decades) -> Debrief (real compound-interest math + save).
//
// All simulated returns are simplified, illustrative averages for teaching
// purposes — not financial advice, and not a promise of real-world results.

export const HORIZON_OPTIONS = [10, 20, 30];
export const CONTRIBUTION_OPTIONS = [50, 100, 150, 200, 300];

// ---------------------------------------------------------------------------
// STAGE 1 — RECON: five real ways people invest $ every day. Risk and Fees
// are pre-computed facts about each vehicle; the player's job is to
// correctly read them, which is the whole skill of comparing investments.
// ---------------------------------------------------------------------------
export const FUND_CANDIDATES = [
  {
    id: "index",
    name: "Total Market Index Fund",
    description: "Owns a slice of the 500 largest U.S. companies at once. Expense ratio: 0.03%.",
    expenseRatio: 0.03,
    netAnnualReturn: 9.5,
    correctRisk: "LOW",
    correctFees: "LOW",
    flavor: "Boring on purpose — diversified across hundreds of companies, so no single failure sinks it.",
  },
  {
    id: "active",
    name: "ActiveGrowth Mutual Fund",
    description: "A team of professionals hand-picks stocks, trying to beat the market. Expense ratio: 1.2%.",
    expenseRatio: 1.2,
    netAnnualReturn: 7.0,
    correctRisk: "MEDIUM",
    correctFees: "HIGH",
    flavor: "Skilled people, real fees — and most active funds still don't beat a plain index over time.",
  },
  {
    id: "savings",
    name: "Grandma's Savings Account",
    description: "FDIC-insured, can't lose a dollar, pays about 0.5% a year.",
    expenseRatio: 0,
    netAnnualReturn: 0.5,
    correctRisk: "LOW",
    correctFees: "LOW",
    flavor: "Safe from loss, but not from inflation quietly eating its value every year.",
  },
  {
    id: "stock",
    name: "TechRocket Inc. (single stock)",
    description: "One company, no diversification. Could double. Could go to zero.",
    expenseRatio: 0,
    netAnnualReturn: 4.0,
    correctRisk: "HIGH",
    correctFees: "LOW",
    flavor: "Zero fund fees — but your entire outcome now rides on one company's decisions.",
  },
  {
    id: "meme",
    name: "Meme Coin",
    description: "No company, no earnings, no index — just a token and a lot of hype.",
    expenseRatio: 0,
    netAnnualReturn: -2.0,
    correctRisk: "HIGH",
    correctFees: "LOW",
    flavor: "Most of these eventually go to zero. The ones people remember are the rare exception, not the rule.",
  },
];

export const fundById = Object.fromEntries(FUND_CANDIDATES.map((f) => [f.id, f]));

export const RISK_OPTIONS = [
  { id: "LOW", label: "Low" },
  { id: "MEDIUM", label: "Medium" },
  { id: "HIGH", label: "High" },
];

export const FEE_OPTIONS = [
  { id: "LOW", label: "Low" },
  { id: "HIGH", label: "High" },
];

// ---------------------------------------------------------------------------
// STAGE 3 — CAMPAIGN: six milestones across the journey. Each choice can
// change the contribution, the return, a lump sum, or a years-lost penalty
// that feeds directly into the Debrief's real compound-interest math.
// ---------------------------------------------------------------------------
export const ENCOUNTERS = [
  {
    id: "e1",
    year: "Year 2",
    title: "First Wobble",
    text: "Just after you start, the market dips 15%. Financial news is loud about it.",
    choices: [
      { id: "hold", label: "Stay the course — keep contributing", tag: "good", result: "You just bought more shares at a lower price. Dollar-cost averaging works in both directions." },
      { id: "pause", label: "Pause contributions until it calms down", tag: "bad", result: "You sat out the exact window shares were cheapest.", effect: { contributionDelta: 0, yearsPenalty: 1 } },
      { id: "sell", label: "Sell everything, move to cash", tag: "bad", result: "You locked in the loss and stepped out of the recovery that followed.", effect: { yearsPenalty: 3 } },
    ],
  },
  {
    id: "e2",
    year: "Year 4",
    title: "The Hot Tip",
    text: "A friend swears a meme coin is about to 10x. \u201cThis is the one,\u201d they say.",
    choices: [
      { id: "stay", label: "Stay diversified — stick with the plan", tag: "good", result: "No regrets when it craters 80% two months later." },
      { id: "chase", label: "Redirect this month's contribution into it", tag: "bad", result: "It didn't 10x. It's down 60% and counting.", effect: { principalHit: 800 } },
    ],
  },
  {
    id: "e3",
    year: "Year 7",
    title: "The Advisor's Pitch",
    text: "A full-service advisor offers to actively manage your account for a 1.5% annual fee, promising to beat the market.",
    choices: [
      { id: "decline", label: "Decline — that fee compounds against you too", tag: "good", result: "You kept the low-cost engine running exactly as planned." },
      { id: "switch", label: "Switch to the managed account", tag: "bad", result: "The fee quietly outpaced the extra performance, most years.", effect: { returnOverride: 6.5 } },
    ],
  },
  {
    id: "e4",
    year: "Year 10",
    title: "The Raise",
    text: "You get a solid raise at work.",
    choices: [
      { id: "increase", label: "Increase your monthly contribution by $50", tag: "good", result: "Future-you just got a raise too.", effect: { contributionDelta: 50 } },
      { id: "same", label: "Keep contributing the same amount", tag: "neutral", result: "No harm done — just a smaller boost to the snowball than it could've been." },
    ],
  },
  {
    id: "e5",
    year: "Year 15",
    title: "Tax Refund",
    text: "A $2,000 tax refund lands in your account.",
    choices: [
      { id: "invest", label: "Invest it as a lump sum", tag: "good", result: "That $2,000 now has years left to compound.", effect: { lumpSum: 2000 } },
      { id: "spend", label: "Spend it on something fun", tag: "neutral", result: "Fair enough — it was a nice refund." },
    ],
  },
  {
    id: "e6",
    year: "Year 20",
    title: "The Big Crash",
    text: "A serious downturn hits — headlines are calling it the worst in over a decade.",
    choices: [
      { id: "hold", label: "Hold. You're investing for decades, not months.", tag: "good", result: "The recovery came, like it always eventually has." },
      { id: "sell", label: "Sell to protect what you've built", tag: "bad", result: "You turned a paper dip into a real, permanent loss.", effect: { yearsPenalty: 5 } },
    ],
  },
];

// ---------------------------------------------------------------------------
// COMPOUND-INTEREST MATH
// ---------------------------------------------------------------------------

// Future value of a monthly contribution stream (annuity due — contribution
// lands at the start of each month), compounded monthly.
export function futureValueOfContributions(monthlyContribution, annualReturnPct, months) {
  const r = annualReturnPct / 100 / 12;
  if (months <= 0) return 0;
  if (r === 0) return monthlyContribution * months;
  return monthlyContribution * ((Math.pow(1 + r, months) - 1) / r) * (1 + r);
}

// Future value of a single lump sum invested partway through the journey.
export function futureValueOfLumpSum(amount, annualReturnPct, yearsInvested) {
  if (yearsInvested <= 0) return amount;
  return amount * Math.pow(1 + annualReturnPct / 100, yearsInvested);
}

export function computeEffectiveReturn({ baseReturn, reinvestDividends, taxAdvantaged, returnOverride }) {
  let r = returnOverride ?? baseReturn;
  if (!reinvestDividends) r -= 1.5;
  if (!taxAdvantaged) r -= 0.4;
  return r;
}
